/**
 * MezziTerrestriLayer.tsx — Marker mezzi terrestri sulla mappa SI.TA.C.
 *
 * © 2024-2025 IRTILAC — Angelo Marino — Tutti i diritti riservati.
 */

import React from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import type { MezzoTerrestre, TipoMezzoTerrestre } from '../../types';

// ── Emoji per tipo mezzo ───────────────────────────────────────
const TIPO_EMOJI: Record<TipoMezzoTerrestre, string> = {
  autobotte:       '🚒',
  aps:             '🚑',
  pick_up:         '🛻',
  modulo_boschivo: '🌿',
  squadra:         '👥',
  squadra_aib:     '🔥',
  altro:           '🚗',
};

const TIPO_LABEL: Record<TipoMezzoTerrestre, string> = {
  autobotte:       'Autobotte',
  aps:             'APS',
  pick_up:         'Pick-up',
  modulo_boschivo: 'Modulo boschivo',
  squadra:         'Squadra',
  squadra_aib:     'Squadra AIB',
  altro:           'Altro',
};

// ── Colore per posizione operativa ────────────────────────────
const POS_COLOR: Record<string, string> = {
  IN_TESTA:    '#f97316',
  FIANCO:      '#3b82f6',
  BONIFICA:    '#8b5cf6',
  SERBATOIO:   '#06b6d4',
  RISERVA:     '#6b7280',
  BASE:        '#64748b',
};

function getMezzoColor(m: MezzoTerrestre): string {
  return POS_COLOR[m.posizioneOperativa] ?? '#64748b';
}

// ── Icona DivIcon ──────────────────────────────────────────────
function buildMezzoIcon(m: MezzoTerrestre): L.DivIcon {
  const color = getMezzoColor(m);
  const emoji = TIPO_EMOJI[m.tipo] ?? '🚗';
  const label = m.sigla || m.targa || '';

  const labelHtml = label
    ? `<div style="position:absolute;bottom:-18px;left:50%;transform:translateX(-50%);background:rgba(15,23,42,0.88);color:#e2e8f0;font-size:8.5px;padding:2px 6px;border-radius:3px;white-space:nowrap;pointer-events:none;font-weight:700;letter-spacing:0.3px;border:1px solid rgba(255,255,255,0.12);">${label}</div>`
    : '';

  const html = `
    <div style="position:relative;width:34px;height:34px;">
      <div style="
        width:34px;height:34px;
        background:${color};
        border:2.5px solid rgba(255,255,255,0.9);
        border-radius:6px;
        display:flex;align-items:center;justify-content:center;
        font-size:17px;line-height:1;
        box-shadow:0 2px 10px rgba(0,0,0,0.55);
        user-select:none;
      ">${emoji}</div>
      ${labelHtml}
    </div>`;

  return L.divIcon({ html, iconSize: [34, 34], iconAnchor: [17, 17], className: '' });
}

// ── Componente ─────────────────────────────────────────────────
interface MezziTerrestriLayerProps {
  mezzi: MezzoTerrestre[];
  onMoveMezzo: (id: string, lat: number, lng: number) => void;
  onRimuoviDallaMappa: (id: string) => void;
  readOnly?: boolean;
}

const MezziTerrestriLayer: React.FC<MezziTerrestriLayerProps> = ({
  mezzi,
  onMoveMezzo,
  onRimuoviDallaMappa,
  readOnly = false,
}) => {
  const mezziPositionati = mezzi.filter((m) => m.lat !== undefined && m.lng !== undefined);

  return (
    <>
      {mezziPositionati.map((m) => (
        <Marker
          key={m.id}
          position={[m.lat!, m.lng!]}
          icon={buildMezzoIcon(m)}
          draggable={!readOnly}
          eventHandlers={{
            dragend(e) {
              const { lat, lng } = e.target.getLatLng();
              onMoveMezzo(m.id, lat, lng);
            },
          }}
        >
          <Popup>
            <div style={{ minWidth: 160, fontFamily: 'sans-serif', fontSize: '0.85rem' }}>
              <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 4 }}>
                {TIPO_EMOJI[m.tipo]} {m.sigla}
              </div>
              <div style={{ color: '#475569', marginBottom: 2 }}>
                <strong>Tipo:</strong> {TIPO_LABEL[m.tipo]}
              </div>
              {m.targa && (
                <div style={{ color: '#475569', marginBottom: 2 }}>
                  <strong>Targa:</strong> {m.targa}
                </div>
              )}
              {m.ente && (
                <div style={{ color: '#475569', marginBottom: 2 }}>
                  <strong>Ente:</strong> {m.ente}
                </div>
              )}
              {m.autista && (
                <div style={{ color: '#475569', marginBottom: 2 }}>
                  <strong>Autista:</strong> {m.autista}
                </div>
              )}
              {m.capoSquadra && (
                <div style={{ color: '#475569', marginBottom: 2 }}>
                  <strong>Capo squadra:</strong> {m.capoSquadra}
                </div>
              )}
              {m.numeroComponenti !== undefined && (
                <div style={{ color: '#475569', marginBottom: 2 }}>
                  <strong>Componenti:</strong> {m.numeroComponenti}
                </div>
              )}
              <div style={{ color: '#475569', marginBottom: 8 }}>
                <strong>Pos.:</strong> {m.posizioneOperativa}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: 8 }}>
                {m.lat!.toFixed(6)}, {m.lng!.toFixed(6)}
              </div>
              {!readOnly && (
                <button
                  onClick={() => onRimuoviDallaMappa(m.id)}
                  style={{
                    width: '100%', padding: '4px 8px', background: '#ef4444',
                    color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer',
                    fontSize: '0.78rem', fontWeight: 600,
                  }}
                >
                  Rimuovi dalla mappa
                </button>
              )}
            </div>
          </Popup>
        </Marker>
      ))}
    </>
  );
};

export default MezziTerrestriLayer;
